/**
 * Embeddable Chat Widget
 *
 * A standalone chat component that can be embedded anywhere.
 * Uses the Public Chat REST API (ChatApiStack) and requires no authentication.
 */

import React, {
  useState,
  useEffect,
  useRef,
  Component,
  ErrorInfo,
  ReactNode,
} from "react";
import ReactMarkdown from "react-markdown";
import type { ChatMessage, ChatSession } from "../../services/chat-service";
import { PublicChatApi } from "../../services/public-chat-api";

// Utility function to detect if a URL points to a PDF
const isPdfUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname.toLowerCase();

    // Check if URL ends with .pdf
    if (pathname.endsWith(".pdf")) {
      return true;
    }

    // Check if URL contains PDF-related paths
    if (pathname.includes("/pdf/") || pathname.includes(".pdf?")) {
      return true;
    }

    // Check for S3 URLs with PDF content type indicators
    if (
      pathname.includes("pdf") &&
      (url.includes("s3.amazonaws.com") || url.includes("cloudfront.net"))
    ) {
      return true;
    }

    return false;
  } catch {
    return false;
  }
};

// Utility function to detect and convert YouTube URLs to embeddable format
const isYouTubeUrl = (url: string): boolean => {
  try {
    const urlObj = new URL(url);
    return (
      urlObj.hostname.includes("youtube.com") ||
      urlObj.hostname.includes("youtu.be")
    );
  } catch {
    return false;
  }
};

const convertToYouTubeEmbedUrl = (url: string): string => {
  try {
    const urlObj = new URL(url);

    // Handle youtu.be short URLs
    if (urlObj.hostname.includes("youtu.be")) {
      const videoId = urlObj.pathname.slice(1); // Remove leading slash
      return `https://www.youtube.com/embed/${videoId}`;
    }

    // Handle youtube.com URLs
    if (urlObj.hostname.includes("youtube.com")) {
      // Handle /watch?v=VIDEO_ID format
      const videoId = urlObj.searchParams.get("v");
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      }

      // Handle /embed/VIDEO_ID format (already embeddable)
      if (urlObj.pathname.includes("/embed/")) {
        return url;
      }
    }

    // Return original URL if we can't parse it
    return url;
  } catch {
    return url;
  }
};

// Simple Error Boundary Component
interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class ChatErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Chat widget error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            padding: "20px",
            textAlign: "center",
            backgroundColor: "#fee2e2",
            color: "#991b1b",
            borderRadius: "8px",
            margin: "20px",
          }}
        >
          <h3>Chat temporarily unavailable</h3>
          <p>Please refresh the page to try again.</p>
          <button
            onClick={() => this.setState({ hasError: false })}
            style={{
              padding: "8px 16px",
              backgroundColor: "#dc2626",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              marginTop: "10px",
            }}
          >
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

interface EmbeddableChatWidgetProps {
  campaignId?: string;
  assetId?: string;
  conversationId?: string;
  trackingCode?: string;
  autoResponderId?: string;
  startMessage?: string;
  prefillMessage?: string;
  prefillOnFirstSession?: boolean;
  deviceType?: "PHONE" | "MOBILE" | "DESKTOP" | "TABLET" | "OTHER";
  source?: string;
  embedded?: boolean;
  containerStyle?: React.CSSProperties;
  customerInfo?: {
    name?: string;
    email?: string;
    phoneNumber?: string;
  };
  theme?: {
    primaryColor?: string;
    backgroundColor?: string;
    textColor?: string;
  };
  position?: "bottom-right" | "bottom-left" | "top-right" | "top-left";
  minimized?: boolean;
  tenantId?: string; // Optional explicit tenant for Public Chat API
  showDestinationIframe?: boolean; // New prop to enable iframe layout
}

export const EmbeddableChatWidget: React.FC<EmbeddableChatWidgetProps> = ({
  assetId,
  conversationId,
  trackingCode,
  startMessage,
  prefillMessage,
  prefillOnFirstSession = false,
  deviceType = "DESKTOP",
  embedded = false,
  containerStyle,
  showDestinationIframe = false,
  theme = {
    primaryColor: "#007bff",
    backgroundColor: "#ffffff",
    textColor: "#333333",
  },
  tenantId,
}) => {
  // ALL HOOKS MUST BE DECLARED FIRST - BEFORE ANY EARLY RETURNS
  // Prevent hydration mismatches by ensuring client-side rendering
  const [isClient, setIsClient] = useState(false);
  const [session, setSession] = useState<ChatSession | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [destinationUrl, setDestinationUrl] = useState<string | null>(null);
  const [destinationName, setDestinationName] = useState<string | null>(null);
  const [conversationData, setConversationData] = useState<any>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Set client-side rendering flag
  useEffect(() => {
    setIsClient(true);
  }, []);

  // No Apollo Client mutations needed - using ChatService directly

  // Key for resuming sessions per conversation + tracking
  const sessionStorageKey =
    conversationId || trackingCode
      ? `chat:session:${conversationId || ""}:${trackingCode || ""}`
      : undefined;

  // Try to resume
  useEffect(() => {
    if (!embedded) return;
    (async () => {
      if (!session && sessionStorageKey) {
        const existing = localStorage.getItem(sessionStorageKey);
        if (existing) {
          const existingSessionId = existing;
          try {
            const api = new PublicChatApi();
            // Load cached tenant if present
            const cachedTenant =
              localStorage.getItem(
                `chat:tenant:${conversationId || ""}:${trackingCode || ""}`
              ) || undefined;
            if (cachedTenant) api.setTenantId(cachedTenant);
            const existingSession = await api.getSession(
              existingSessionId,
              cachedTenant || undefined
            );
            if (existingSession) {
              // Cache server-confirmed tenantId
              if (existingSession.tenantId) {
                localStorage.setItem(
                  `chat:tenant:${conversationId || ""}:${trackingCode || ""}`,
                  existingSession.tenantId as any
                );
                api.setTenantId(existingSession.tenantId as any);
              }
              setSession(existingSession);
              await loadMessages(existingSessionId);
              return;
            }
          } catch {}
          localStorage.removeItem(sessionStorageKey);
        }
      }
    })();
  }, [embedded, sessionStorageKey]);
  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Fetch conversation data when component mounts
  useEffect(() => {
    const fetchConversationData = async () => {
      if (!conversationId || !showDestinationIframe) return;

      try {
        const api = new PublicChatApi();
        const conversation = await api.getConversation(conversationId);

        if (conversation) {
          setConversationData(conversation);

          // Get the first active destination URL
          const activeDestination = conversation.destinations?.find(
            (dest: any) => dest && dest.isActive && dest.url
          );

          if (activeDestination) {
            setDestinationUrl(activeDestination.url);
            setDestinationName(activeDestination.name);
          }
        }
      } catch (err) {
        console.warn("Failed to fetch conversation data:", err);
      }
    };

    fetchConversationData();
  }, [conversationId, showDestinationIframe]);

  // Initialize chat session
  const initializeSession = async () => {
    if (session) return;

    setIsLoading(true);
    setError(null);

    try {
      const allowedDevices = new Set(["MOBILE", "DESKTOP", "TABLET", "OTHER"]);
      const normalizedDevice = allowedDevices.has(deviceType as any)
        ? (deviceType as any)
        : ("DESKTOP" as const);

      // Create via Public Chat API (requires tenantId header)
      const api = new PublicChatApi();

      const userAgent =
        typeof navigator !== "undefined" ? navigator.userAgent : "unknown";

      const newSession = await api.createSession({
        tenantId, // optional; server resolves via tracking/session
        conversationId: conversationId || undefined,
        assetId: assetId || undefined,
        trackingCode: trackingCode || undefined,
        deviceType: normalizedDevice,
        userAgent,
      });
      // Cache tenant from server
      if (newSession.tenantId) {
        localStorage.setItem(
          `chat:tenant:${conversationId || ""}:${trackingCode || ""}`,
          newSession.tenantId as any
        );
        api.setTenantId(newSession.tenantId as any);
      }
      setSession(newSession);

      // Persist for resume
      if (sessionStorageKey) {
        localStorage.setItem(sessionStorageKey, newSession.sessionId);
      }

      // Load existing messages
      await loadMessages(newSession.sessionId);

      // Prefill on first session if requested
      if (prefillOnFirstSession && (prefillMessage || startMessage)) {
        const initial = (prefillMessage || startMessage || "").trim();
        if (initial) {
          try {
            const api = new PublicChatApi();
            await api.sendMessage(newSession.sessionId, initial, "TEXT");
            // WebSocket will handle adding the message to the UI
          } catch (e) {
            console.warn("Prefill send failed", e);
          }
        }
      }
    } catch (err: any) {
      console.error("Failed to initialize chat session:", err);
      // Surface a friendly banner error to help debug
      const errMsg =
        (err?.message as string) ||
        (typeof err === "string" ? err : "Failed to initialize chat session");
      setError(`${errMsg}`);

      // Fallback to local session so the widget remains usable offline / in tests
      const fallbackSession: ChatSession = {
        sessionId: `local-${Date.now()}`,
        conversationId: conversationId || "",
        status: "ACTIVE",
        aiEnabled: false,
        deviceType: (deviceType as any) || "DESKTOP",
        startTime: new Date().toISOString(),
        lastActivity: new Date().toISOString(),
        messageCount: 0,
      };
      setSession(fallbackSession);
      if (sessionStorageKey) {
        localStorage.setItem(sessionStorageKey, fallbackSession.sessionId);
      }
      // Note: For fallback sessions, we don't send prefill messages since there's no server connection
    } finally {
      setIsLoading(false);
    }
  };

  // Load messages for the session
  const loadMessages = async (sessionId: string) => {
    if (!sessionId) {
      console.warn("Cannot load messages: sessionId is required");
      return;
    }

    try {
      const api = new PublicChatApi();
      const cachedTenant =
        localStorage.getItem(
          `chat:tenant:${conversationId || ""}:${trackingCode || ""}`
        ) || undefined;
      if (cachedTenant) api.setTenantId(cachedTenant);
      const chatMessages = await api.getMessages(
        sessionId,
        cachedTenant || undefined
      );
      setMessages(chatMessages || []);
    } catch (err) {
      console.error("Failed to load messages:", err);
      setError("Failed to load messages");
    }
  };

  // Send a message
  const sendMessage = async () => {
    if (!newMessage.trim() || !session || isLoading) return;

    const messageContent = newMessage.trim();
    setNewMessage("");
    setIsLoading(true);

    try {
      const api = new PublicChatApi();
      const cachedTenant =
        localStorage.getItem(
          `chat:tenant:${conversationId || ""}:${trackingCode || ""}`
        ) || undefined;
      if (cachedTenant) api.setTenantId(cachedTenant);
      const result = await api.sendMessage(
        session.sessionId,
        messageContent,
        "TEXT",
        cachedTenant || undefined
      );

      if (!result.success) {
        setError(result.error || "Failed to send message");
      }
      // Note: We rely on WebSocket for real-time message updates, no need to reload messages
    } catch (err) {
      console.error("Failed to send message:", err);
      setError("Failed to send message. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Load initial messages when session becomes available and open WS for realtime
  useEffect(() => {
    if (!session?.sessionId) return;
    loadMessages(session.sessionId);

    const api = new PublicChatApi();
    api.ensureConnected(session.sessionId, (msg) => {
      setMessages((prev) => [...prev, msg]);
    });
  }, [session?.sessionId]);

  // Auto initialize if embedded (but only if no session exists and we haven't tried to resume)
  useEffect(() => {
    if (embedded && !session && sessionStorageKey) {
      // Check if we already tried to resume in the earlier useEffect
      const hasTriedResume = localStorage.getItem(sessionStorageKey);
      if (!hasTriedResume) {
        initializeSession();
      }
    } else if (embedded && !session && !sessionStorageKey) {
      // No conversation/tracking info, create session immediately
      initializeSession();
    }
  }, [embedded, session, sessionStorageKey]);

  // Early return for client-side rendering (after all hooks are declared)
  if (!isClient) {
    return (
      <div
        style={{
          padding: "20px",
          textAlign: "center",
          fontFamily: "system-ui, -apple-system, sans-serif",
        }}
      >
        Loading chat...
      </div>
    );
  }

  if (embedded) {
    // Check if we should show the iframe layout
    if (showDestinationIframe && destinationUrl) {
      // Iframe + Chat layout: destination on left, chat on right
      return (
        <div
          style={{
            width: "100vw",
            height: "100vh",
            display: "flex",
            flexDirection: "row",
            fontFamily: "system-ui, -apple-system, sans-serif",
            backgroundColor: "#f8fafc",
            ...containerStyle,
          }}
        >
          {/* Left column - Content/PDF/iframe */}
          <div
            style={{
              flex: 1,
              height: "100vh",
              backgroundColor: "#ffffff",
              overflow: "hidden",
            }}
          >
            {/* Content container */}
            {destinationUrl ? (
              isPdfUrl(destinationUrl) ? (
                // For PDFs, use object/embed instead of sandboxed iframe
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <object
                    data={`${destinationUrl}#zoom=FitH&view=FitH`}
                    type="application/pdf"
                    style={{
                      width: "100%",
                      height: "100%",
                      border: "none",
                    }}
                  >
                    <embed
                      src={`${destinationUrl}#zoom=FitH&view=FitH`}
                      type="application/pdf"
                      style={{
                        width: "100%",
                        height: "100%",
                        border: "none",
                      }}
                    />
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#f8fafc",
                        color: "#64748b",
                        textAlign: "center",
                        padding: "20px",
                      }}
                    >
                      <p style={{ marginBottom: "16px" }}>
                        PDF cannot be displayed in this browser.
                      </p>
                      <a
                        href={destinationUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#3b82f6",
                          textDecoration: "none",
                          padding: "8px 16px",
                          border: "1px solid #3b82f6",
                          borderRadius: "6px",
                          backgroundColor: "white",
                        }}
                      >
                        Download PDF
                      </a>
                    </div>
                  </object>
                </div>
              ) : isYouTubeUrl(destinationUrl) ? (
                // For YouTube videos, use embed URL format
                <iframe
                  src={convertToYouTubeEmbedUrl(destinationUrl)}
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                  }}
                  title={destinationName || "YouTube Video"}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  onError={(e) => {
                    console.warn("YouTube iframe failed to load:", e);
                  }}
                />
              ) : (
                // For other URLs, use standard iframe with sandbox
                <iframe
                  src={destinationUrl}
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                  }}
                  title={destinationName || "Destination Content"}
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                  onError={(e) => {
                    console.warn("Iframe failed to load:", e);
                  }}
                />
              )
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#f8fafc",
                  color: "#64748b",
                }}
              >
                Loading destination...
              </div>
            )}
          </div>

          {/* Right column - Chat sidebar */}
          <div
            style={{
              width: "500px",
              height: "100vh",
              backgroundColor: "#ffffff",
              borderLeft: "1px solid #e2e8f0",
              display: "flex",
              flexDirection: "column",
              boxShadow: "-4px 0 6px -1px rgba(0, 0, 0, 0.1)",
            }}
          >
            {/* Chat Header with Logo */}
            <div
              style={{
                backgroundColor: "#000000",
                color: "white",
                padding: "20px",
                borderBottom: "1px solid #e2e8f0",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  marginBottom: "8px",
                }}
              >
                <img
                  src="/img/activQMS.svg"
                  alt="activQMS"
                  style={{
                    height: "24px",
                    width: "auto",
                  }}
                />
                <div
                  style={{
                    width: "8px",
                    height: "8px",
                    backgroundColor: "#10b981",
                    borderRadius: "50%",
                  }}
                />
                <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "600" }}>
                  Chat
                </h3>
              </div>
              {destinationName && (
                <div
                  style={{
                    fontSize: "14px",
                    color: "#9ca3af",
                    fontStyle: "italic",
                  }}
                >
                  Ask me anything about {destinationName}
                </div>
              )}
            </div>

            {/* Messages */}
            <div
              style={{
                flex: 1,
                padding: "20px",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                backgroundColor: "#f8fafc",
              }}
            >
              {error && (
                <div
                  style={{
                    backgroundColor: "#fef2f2",
                    color: "#dc2626",
                    padding: "12px 16px",
                    borderRadius: "8px",
                    fontSize: "14px",
                    border: "1px solid #fecaca",
                  }}
                >
                  {error}
                </div>
              )}

              {messages && Array.isArray(messages)
                ? messages
                    .filter((_, index) => {
                      // Hide the first message in chat UI (start message)
                      // This is only for chat channels, not SMS
                      return index !== 0;
                    })
                    .map((message) => {
                      if (!message || !message.id) {
                        console.warn("Invalid message object:", message);
                        return null;
                      }

                      return (
                        <div
                          key={message.id}
                          style={{
                            alignSelf:
                              message.sender === "user"
                                ? "flex-end"
                                : "flex-start",
                            maxWidth: "85%",
                          }}
                        >
                          <div
                            style={{
                              backgroundColor:
                                message.sender === "user"
                                  ? "#6366f1"
                                  : "#ffffff",
                              color:
                                message.sender === "user"
                                  ? "white"
                                  : theme.textColor,
                              padding: "12px 16px",
                              borderRadius: "16px",
                              fontSize: "14px",
                              lineHeight: "1.5",
                              wordWrap: "break-word",
                              boxShadow:
                                message.sender === "user"
                                  ? "0 2px 4px rgba(0,0,0,0.1)"
                                  : "0 2px 4px rgba(0,0,0,0.05)",
                              border:
                                message.sender === "user"
                                  ? "none"
                                  : "1px solid #e2e8f0",
                            }}
                          >
                            {/* Render markdown for AI responses, HTML for system messages, plain text for users */}
                            {message.sender === "bot" ||
                            (message.metadata &&
                              message.metadata.type === "ai-response") ? (
                              <ReactMarkdown
                                components={{
                                  // Customize markdown rendering for chat
                                  p: ({ children }) => <span>{children}</span>,
                                  strong: ({ children }) => (
                                    <strong style={{ fontWeight: "bold" }}>
                                      {children}
                                    </strong>
                                  ),
                                  em: ({ children }) => (
                                    <em style={{ fontStyle: "italic" }}>
                                      {children}
                                    </em>
                                  ),
                                  ul: ({ children }) => (
                                    <ul
                                      style={{
                                        margin: "4px 0",
                                        paddingLeft: "16px",
                                      }}
                                    >
                                      {children}
                                    </ul>
                                  ),
                                  ol: ({ children }) => (
                                    <ol
                                      style={{
                                        margin: "4px 0",
                                        paddingLeft: "16px",
                                      }}
                                    >
                                      {children}
                                    </ol>
                                  ),
                                  li: ({ children }) => (
                                    <li style={{ margin: "2px 0" }}>
                                      {children}
                                    </li>
                                  ),
                                  code: ({ children }) => (
                                    <code
                                      style={{
                                        backgroundColor: "rgba(0,0,0,0.1)",
                                        padding: "2px 4px",
                                        borderRadius: "3px",
                                        fontSize: "0.9em",
                                      }}
                                    >
                                      {children}
                                    </code>
                                  ),
                                  a: ({ href, children }) => {
                                    // Check if this is a Calendly link for appointment booking
                                    if (href && href.includes("calendly.com")) {
                                      return (
                                        <div
                                          style={{
                                            display: "block",
                                            textAlign: "center",
                                            margin: "12px 0",
                                          }}
                                        >
                                          <button
                                            onClick={() =>
                                              window.open(href, "_blank")
                                            }
                                            style={{
                                              backgroundColor: "#6500ff", // activQMS purple
                                              color: "white",
                                              border: "none",
                                              padding: "12px 24px",
                                              borderRadius: "8px",
                                              fontSize: "16px",
                                              fontWeight: "600",
                                              cursor: "pointer",
                                              textDecoration: "none",
                                              display: "inline-block",
                                              transition: "all 0.2s ease",
                                              boxShadow:
                                                "0 2px 8px rgba(101, 0, 255, 0.2)",
                                            }}
                                            onMouseOver={(e) => {
                                              e.currentTarget.style.opacity =
                                                "0.9";
                                              e.currentTarget.style.transform =
                                                "translateY(-1px)";
                                              e.currentTarget.style.boxShadow =
                                                "0 4px 12px rgba(101, 0, 255, 0.3)";
                                            }}
                                            onMouseOut={(e) => {
                                              e.currentTarget.style.opacity =
                                                "1";
                                              e.currentTarget.style.transform =
                                                "translateY(0)";
                                              e.currentTarget.style.boxShadow =
                                                "0 2px 8px rgba(101, 0, 255, 0.2)";
                                            }}
                                          >
                                            📅 {children || "Book Now"}
                                          </button>
                                        </div>
                                      );
                                    }
                                    // Regular links
                                    return (
                                      <a
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          color: theme.primaryColor,
                                          textDecoration: "underline",
                                        }}
                                      >
                                        {children}
                                      </a>
                                    );
                                  },
                                }}
                              >
                                {message.content}
                              </ReactMarkdown>
                            ) : message.sender === "system" ? (
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: message.content,
                                }}
                              />
                            ) : (
                              message.content
                            )}
                          </div>
                          <div
                            style={{
                              fontSize: "11px",
                              color: "#94a3b8",
                              marginTop: "6px",
                              textAlign:
                                message.sender === "user" ? "right" : "left",
                            }}
                          >
                            {message.timestamp.toLocaleTimeString([], {
                              hour: "numeric",
                              minute: "2-digit",
                            })}
                          </div>
                        </div>
                      );
                    })
                    .filter(Boolean)
                : null}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div
              style={{
                padding: "20px",
                borderTop: "1px solid #e2e8f0",
                backgroundColor: "#ffffff",
                display: "flex",
                gap: "12px",
              }}
            >
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                placeholder="Type your message..."
                disabled={isLoading}
                style={{
                  flex: 1,
                  padding: "12px 16px",
                  border: "1px solid #d1d5db",
                  borderRadius: "24px",
                  outline: "none",
                  fontSize: "14px",
                  backgroundColor: "#f9fafb",
                  transition: "border-color 0.2s",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#6366f1";
                  e.target.style.backgroundColor = "#ffffff";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#d1d5db";
                  e.target.style.backgroundColor = "#f9fafb";
                }}
              />
              <button
                onClick={sendMessage}
                disabled={isLoading || !newMessage.trim()}
                style={{
                  backgroundColor: "#6366f1",
                  color: "white",
                  border: "none",
                  borderRadius: "50%",
                  width: "44px",
                  height: "44px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: isLoading || !newMessage.trim() ? 0.5 : 1,
                  transition: "all 0.2s",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
                onMouseEnter={(e) => {
                  if (!isLoading && newMessage.trim()) {
                    e.currentTarget.style.transform = "scale(1.05)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                }}
              >
                {isLoading ? "..." : "→"}
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Fallback to original embedded layout if no destination URL
    return (
      <div
        style={{
          maxWidth: 640,
          margin: "40px auto",
          padding: "0 16px",
          fontFamily: "system-ui, -apple-system, sans-serif",
          ...containerStyle,
        }}
      >
        <div
          style={{
            width: "100%",
            height: "70vh",
            minHeight: 420,
            backgroundColor: theme.backgroundColor,
            borderRadius: "12px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Original embedded chat layout would go here */}
          <div style={{ padding: "20px", textAlign: "center" }}>
            <p>Chat widget loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // Non-embedded mode: not used on /chat. Return null by default.
  return null;
};

// Wrapper component with error boundary
const EmbeddableChatWidgetWithErrorBoundary: React.FC<
  EmbeddableChatWidgetProps
> = (props) => {
  return (
    <ChatErrorBoundary>
      <EmbeddableChatWidget {...props} />
    </ChatErrorBoundary>
  );
};

export default EmbeddableChatWidgetWithErrorBoundary;
